import { WithholdingTransactionType } from "@wingspanhq/bookkeeping/dist/lib/interfaces/withholdingTransaction";
import {
  toWSDateString,
  WSInfiniteScroll,
  WSList,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import flatten from "lodash/flatten";
import React, { useMemo } from "react";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useQueryListInternalAccountTransactions } from "../../../query/banking/queries/useQueryListInternalAccountTransactions";
import {
  useQueryWithholdingTransactions,
  useQueryPendingWithholdings
} from "../../../query/taxes/queries";
import { WSQueries } from "../../../query/WSQuery";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { useModalCancelPendingWithholding } from "../modals/ModalCancelPendingWithholding";

const columns: Array<WSTableColumn> = [
  {
    config: {
      header: {
        text: "Transaction"
      },
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: row => <WSTableCell text={row.data.title} />
  },
  {
    config: {
      header: {
        text: "Date"
      },
      gridTemplateSizeMax: "0.3fr"
      // justify: "end"
    },
    renderFunction: row => (
      <WSTableCell text={toWSDateString(row.data.date, "monthDayYear")} />
    )
  },
  {
    config: {
      header: {
        text: "Amount"
      },
      gridTemplateSizeMax: "0.3fr"
      // justify: "end"
    },
    renderFunction: row => (
      <WSText.ParagraphSm
        color="gray600"
        data-testid={`postedTransactionAmount_${row.id}`}
      >
        {formatMoney(row.data.amount, row.data.currency, {
          showSign: true
        })}
      </WSText.ParagraphSm>
    )
  }
];

export const TaxesTransactions: React.FC = () => {
  const modalCancelPendingWithholding = useModalCancelPendingWithholding();

  const queryWithholdingTransactions = useQueryWithholdingTransactions();
  const queryPendingWithholdings = useQueryPendingWithholdings({
    onError: () => {
      // Suppress errors - we'll default to empty array
      // some old internal accounts still have unit bank or
      // legacy internal accounts and that crashes the entire component. instead silently fail
    }
  });

  const queryInternalAccounts = useQueryInternalAccounts();
  const internalAccount = queryInternalAccounts.data?.find(
    selectorActiveTaxWithholdingAccount
  );
  const queryPendingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Pending"
    },
    {
      enabled: !!internalAccount
    }
  );

  const pendingTransactions = useMemo(() => {
    // Get pending transactions from internal account
    const internalAccountPending = (
      queryPendingTransactions.data?.pages || []
    ).flatMap(page => page.transactions);

    // Get pending withholdings from bookkeeping endpoint
    // TODO: This is temporary fix for this tax season, we need to figure out a better way to handle this or change endpoint.
    // remove this merge after we get a unified endpoint for both withdraw and tax transactions
    // ONLY need taxPayment (IRS scheduled payments) from this endpoint
    const withholdingPendingTaxPayments = (
      queryPendingWithholdings.data || []
    ).filter(
      withholding => withholding.type === WithholdingTransactionType.TaxPayment
    );
    const withholdingPending = withholdingPendingTaxPayments.map(
      withholding => ({
        transactionId: withholding.pendingWithholdingId,
        amount: withholding.amount,
        currency: withholding.currency,
        createdAt: withholding.createdAt,
        type: WithholdingTransactionType.TaxPayment
      })
    );

    // Merge both lists and sort by date (newest first)
    return [...internalAccountPending, ...withholdingPending].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [queryPendingTransactions.data?.pages, queryPendingWithholdings.data]);

  return (
    <WSQueries
      queries={{
        queryWithholdingTransactions,
        queryPendingTransactions
      }}
    >
      {({ queryWithholdingTransactionsData }) => {
        const transactionList = flatten(
          queryWithholdingTransactionsData.pages || []
        );

        const tableDataPostedTransactions = transactionList.map(transaction => {
          return {
            data: {
              source: transaction,
              title:
                transaction.type === WithholdingTransactionType.TaxPayment
                  ? "Payment to IRS"
                  : transaction.invoiceNumber
                  ? `Set aside from Invoice #${transaction.invoiceNumber}`
                  : transaction.amount < 0
                  ? "Deposit"
                  : "Withdraw",
              description: transaction.invoiceNumber,
              date: transaction.createdAt,
              amount: transaction.amount * -1,
              currency: transaction.currency
            },
            id: transaction.withholdingTransactionId
          };
        });

        const tableDataPendingTransactions = (pendingTransactions || []).map(
          transaction => {
            return {
              data: {
                source: transaction,
                isPending: true,
                amount: transaction.amount * -1,
                title:
                  transaction.type === WithholdingTransactionType.TaxPayment
                    ? "Scheduled payment to IRS"
                    : transaction.type ===
                      WithholdingTransactionType.TaxWithholding
                    ? "Payment to IRS"
                    : transaction.amount < 0
                    ? "Deposit"
                    : "Withdraw",
                date: transaction.createdAt,
                currency: transaction.currency,
                fromWithholding: true
              },
              id: transaction.transactionId
            };
          }
        );

        if (
          tableDataPendingTransactions.length === 0 &&
          tableDataPostedTransactions.length === 0
        ) {
          return null;
        }

        return (
          <WSList gap="XL">
            <WSText.Paragraph weight="medium">Transactions</WSText.Paragraph>

            <WSTable
              tableData={tableDataPendingTransactions}
              columns={columns}
              showHeader
              forceShowGroups
              getGroupName={() => "Pending"}
              rowMenuActions={item => {
                // Only show cancel option for withholding transactions (not internal account pending)
                const isTaxPayment =
                  item.data?.source.type ===
                  WithholdingTransactionType.TaxPayment;

                if (!isTaxPayment) {
                  return [];
                }

                return [
                  {
                    label: "Cancel",
                    icon: "exit",
                    destructive: true,
                    onClick: () => {
                      modalCancelPendingWithholding.open({
                        pendingWithholdingId: item.data.source.transactionId,
                        amount: item.data.amount,
                        currency: item.data.currency as any,
                        date: item.data.date
                      });
                    }
                  }
                ];
              }}
            />

            <WSInfiniteScroll
              onLoad={() => {
                queryWithholdingTransactions.fetchNextPage();
              }}
              loadMore={transactionList.length > 0}
              endOfList={!queryWithholdingTransactions.hasNextPage}
              loading={!!queryWithholdingTransactions.isFetchingNextPage}
            >
              <WSTable
                tableData={tableDataPostedTransactions}
                columns={columns}
                showHeader={!tableDataPendingTransactions.length}
                forceShowGroups
                getGroupName={() => "Posted"}
                bottomMessage={
                  !tableDataPendingTransactions.length &&
                  !tableDataPostedTransactions.length
                    ? "No activity yet"
                    : undefined
                }
              />
            </WSInfiniteScroll>
          </WSList>
        );
      }}
    </WSQueries>
  );
};
